import React, { useState, useEffect } from "react";
import {
  Users,
  Package,
  FileText,
  DollarSign,
  LogOut,
  TrendingUp,
  Truck,
  CookingPot,
  Warehouse,
  ChevronDown,
  UserCheck,
  Database,
  Wallet,
  HardHat,
  Settings,
  ClipboardList,
  Banknote,
  Receipt,
} from "lucide-react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useAuth } from "./context/AuthContext.js";

import { getBcvRate } from "./utils/api.js";
import { COMPANY_NAME, COMPANY_RIF, COMPANY_SHORT_NAME } from "./utils/company.js";
import BrandLogo from "./components/BrandLogo.js";
import Login from "./modules/Login.js";
import ClientsModule from "./modules/ClientsModule.js";
import ProductsModule from "./modules/ProductsModule.js";
import InvoicesModule from "./modules/InvoicesModule.js";
import DashboardModule from "./modules/DashboardModule.js";
import PaymentsModule from "./modules/PaymentsModule.js";
import ProvidersModule from "./modules/ProvidersModule.js";
import CheesesModule from "./modules/CheesesModule.js";
import MaturationModule from "./modules/MaturationModule.js";
import SellersModule from "./modules/SellersModule.js";
import QueserosModule from "./modules/QueserosModule.js";
import BackupModule from "./modules/BackupModule.js";
import InvoiceActivityModule from "./modules/InvoiceActivityModule.js";
import EmployeesModule from "./modules/EmployeesModule.js";
import PayrollFinanceModule from "./modules/PayrollFinanceModule.js";
import ProviderPaymentsModule from "./modules/ProviderPaymentsModule.js";
import ExpensesModule from "./modules/ExpensesModule.js";
import MobileBottomNav, { MOBILE_PAGE_TITLES } from "./components/MobileBottomNav.js";
import UserAvatar, { ProfileSettingsModal } from "./components/ProfileSettings.js";
import NotificationBell from "./components/NotificationBell.js";
import { NotificationProvider } from "./context/NotificationContext.js";

const BcvRateDisplay = ({ isMobile }) => {
  const [rateInfo, setRateInfo] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await getBcvRate();
        if (res.data.success && res.data.data) {
          setRateInfo(res.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch BCV rate for display:", error);
      }
    };

    fetchRate();
    const interval = setInterval(fetchRate, 1000 * 60 * 60 * 4);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (rateInfo) {
      const timer = setTimeout(() => {
        setIsExpanded(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [rateInfo]);

  if (!rateInfo) {
    return null;
  }

  const positionClass = isMobile
    ? "bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px)+0.5rem)] right-3"
    : "bottom-4 right-4";

  return (
    <div
      className={`page-enter fixed ${positionClass} z-30 flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3 bg-white rounded-2xl border border-gray-200 transition-all duration-300 ease-in-out cursor-pointer max-w-[calc(100vw-1.5rem)] ${isExpanded ? "opacity-100 shadow-lg" : "opacity-60 hover:opacity-100 shadow-md"}`}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <img
        src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/02/Banco_Central_de_Venezuela_logo.svg/120px-Banco_Central_de_Venezuela_logo.svg.png"
        alt="BCV Logo"
        className="h-6 sm:h-7 w-auto flex-shrink-0"
      />
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? "max-w-xs" : "max-w-0"}`}
      >
        <div className="whitespace-nowrap pr-1">
          <p className="text-xs sm:text-sm font-bold text-gray-800">
            Bs.{" "}
            {rateInfo.rate.toLocaleString("es-VE", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
          <p className="text-[10px] sm:text-xs text-gray-400">
            {new Date(rateInfo.date).toLocaleString("es-VE", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

function AppContent() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const [isSalesMenuOpen, setIsSalesMenuOpen] = useState(true);
  const [isProductionMenuOpen, setIsProductionMenuOpen] = useState(true);
  const [isPayrollMenuOpen, setIsPayrollMenuOpen] = useState(true);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading)
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-white to-indigo-50">
        <div className="relative mb-5">
          <span className="splash-ring absolute inset-0 rounded-full bg-indigo-400/30" />
          <BrandLogo className="relative w-16 h-16" />
        </div>
        <div className="h-1 w-28 overflow-hidden rounded-full bg-indigo-100">
          <div className="splash-bar h-full w-2/5 rounded-full bg-indigo-500" />
        </div>
        <p className="mt-4 text-sm font-medium text-gray-500">Cargando {COMPANY_SHORT_NAME}...</p>
      </div>
    );

  if (!user) return <Login />;

  const isAdmin = !user.role || user.role === "admin";
  const isQuesero = user.role === "quesero";
  const canMilk = isAdmin || Boolean(user.permissions?.manageMilk);
  const canCheese = isAdmin || Boolean(user.permissions?.manageCheese);

  const navItems = [
    ...(isAdmin
      ? [
          { id: "dashboard", icon: TrendingUp, label: "Dashboard" },
          { id: "sellers", icon: UserCheck, label: "Vendedores" },
          { id: "queseros", icon: CookingPot, label: "Queseros" },
          { id: "products", icon: Package, label: "Productos" },
        ]
      : []),
  ];
  const salesNavItems = [
    { id: "invoices", icon: FileText, label: "Facturación" },
    { id: "clients", icon: Users, label: isAdmin ? "Clientes" : "Mis clientes" },
    // { id: "payments", icon: DollarSign, label: "Reportes de pago" },
  ];
  const productionNavItems = [
    { id: "providers", icon: Truck, label: "Leche" },
    { id: "cheeses", icon: CookingPot, label: "Quesos" },
    { id: "maturation", icon: Warehouse, label: "Maduración" },
    { id: "provider-payments", icon: Banknote, label: "Movimientos de pago" },
  ];
  const payrollNavItems = [
    { id: "employees", icon: HardHat, label: "Empleados (Queseros)" },
    { id: "payroll", icon: Wallet, label: "Finanzas" },
  ];
  const isSalesSectionActive = ["/invoices", "/payments", "/clients"].includes(currentPath);
  const isProductionSectionActive = ["/providers", "/cheeses", "/maturation"].includes(currentPath);
  const isPayrollSectionActive = ["/employees", "/payroll"].includes(currentPath);
  const defaultHome = isAdmin
    ? "/dashboard"
    : isQuesero
      ? canMilk
        ? "/providers"
        : canCheese
          ? "/cheeses"
          : "/sin-acceso"
      : "/invoices";

  const mobileTitle =
    MOBILE_PAGE_TITLES[currentPath] ||
    MOBILE_PAGE_TITLES["/" + currentPath.split("/")[1]] ||
    COMPANY_SHORT_NAME;

  const NavItem = ({ icon: Icon, label, id }) => {
    const path = `/${id}`;
    const isActive =
      currentPath === path ||
      (path === defaultHome && (currentPath === "/" || currentPath === ""));
    return (
      <Link
        to={path}
        className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 relative ${
          isActive
            ? "bg-indigo-600/15 text-white border border-indigo-500/30"
            : "text-slate-400 hover:text-white hover:bg-slate-800/70"
        }`}
      >
        {isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-indigo-400 rounded-r-full" />
        )}
        <Icon
          size={18}
          className={isActive ? "text-indigo-300" : "text-slate-500 group-hover:text-slate-300"}
        />
        <span>{label}</span>
      </Link>
    );
  };

  const SidebarSubmenu = ({ label, icon: Icon, active, open, onToggle, items }) => (
    <div className="pt-1">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full group flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
          active
            ? "bg-white bg-opacity-10 text-white"
            : "text-slate-400 hover:text-white hover:bg-white hover:bg-opacity-5"
        }`}
      >
        <span className="flex items-center gap-3">
          <Icon
            size={18}
            className={active ? "text-indigo-400" : "text-slate-500 group-hover:text-slate-300"}
          />
          <span>{label}</span>
        </span>
        <ChevronDown
          size={16}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="mt-1 ml-2 pl-2 border-l border-slate-800 space-y-1">
          {items.map((item) => (
            <NavItem key={item.id} {...item} />
          ))}
        </div>
      )}
    </div>
  );

  const shell = (
    <div className="min-h-screen bg-slate-100/70 flex">
      {/* Sidebar — solo escritorio */}
      <aside className="hidden md:flex md:w-64 md:shrink-0 bg-slate-950 flex-col min-h-screen sticky top-0 h-screen">
        <div className="px-5 py-5 flex items-center gap-3 border-b border-slate-800/80">
          <BrandLogo className="w-11 h-11" alt={COMPANY_NAME} />
          <div>
            <h1 className="text-white font-bold text-sm leading-tight">{COMPANY_SHORT_NAME}</h1>
            <p className="text-slate-400 text-[10px] leading-tight">{COMPANY_NAME}</p>
            <p className="text-slate-500 text-[10px]">RIF {COMPANY_RIF}</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <p className="px-3 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Menú
          </p>
          {isAdmin ? (
            navItems.map((item) => (
              <React.Fragment key={item.id}>
                <NavItem {...item} />
                {item.id === "dashboard" && (
                  <SidebarSubmenu
                    label="Ventas"
                    icon={DollarSign}
                    active={isSalesSectionActive}
                    open={isSalesMenuOpen}
                    onToggle={() => setIsSalesMenuOpen((prev) => !prev)}
                    items={salesNavItems}
                  />
                )}
                {item.id === "products" && (
                  <>
                    <SidebarSubmenu
                      label="Producción"
                      icon={Truck}
                      active={isProductionSectionActive}
                      open={isProductionMenuOpen}
                      onToggle={() => setIsProductionMenuOpen((prev) => !prev)}
                      items={productionNavItems}
                    />
                    <NavItem id="expenses" icon={Receipt} label="Gastos" />
                    <SidebarSubmenu
                      label="Nómina"
                      icon={Wallet}
                      active={isPayrollSectionActive}
                      open={isPayrollMenuOpen}
                      onToggle={() => setIsPayrollMenuOpen((prev) => !prev)}
                      items={payrollNavItems}
                    />
                  </>
                )}
              </React.Fragment>
            ))
          ) : isQuesero ? (
            <>
              {canMilk && <NavItem id="providers" icon={Truck} label="Leche" />}
              {canCheese && <NavItem id="cheeses" icon={CookingPot} label="Quesos" />}
            </>
          ) : (
            <>
              {salesNavItems.map((item) => (
                <NavItem key={item.id} {...item} />
              ))}
              {navItems.map((item) => (
                <NavItem key={item.id} {...item} />
              ))}
            </>
          )}
          {isAdmin && (
            <>
              <p className="px-3 mt-5 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Sistema
              </p>
              <NavItem id="invoice-activity" icon={ClipboardList} label="Registro de facturas" />
              <NavItem id="backup" icon={Database} label="Backup" />
            </>
          )}
        </nav>

        <div className="px-3 py-4 border-t border-slate-800/80">
          <div className="flex items-center gap-3 px-3 mb-3">
            <UserAvatar user={user} size="sm" />
            <div className="flex-1 overflow-hidden min-w-0">
              <p className="text-sm text-slate-300 font-medium truncate">
                {user.fullName || user.username}
              </p>
              {user.username && user.fullName ? (
                <p className="text-[11px] text-slate-500 truncate">@{user.username}</p>
              ) : null}
            </div>
            {isAdmin && <NotificationBell align="left" />}
            <button
              type="button"
              onClick={() => setIsProfileOpen(true)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors shrink-0"
              title="Configuración del perfil"
              aria-label="Configuración del perfil"
            >
              <Settings size={16} />
            </button>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-slate-700 transition-colors font-medium"
          >
            <LogOut size={15} /> Cerrar Sesión
          </button>
          <p className="text-[10px] text-slate-500 text-center mt-3 leading-tight">
            {COMPANY_NAME}
            <br />
            RIF {COMPANY_RIF}
          </p>
        </div>
      </aside>

      {/* Contenido principal */}
      <main className="flex-1 flex flex-col min-h-screen min-w-0 w-full md:h-screen md:overflow-hidden">
        <header className="md:hidden sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <BrandLogo className="w-9 h-9" alt={COMPANY_NAME} />
            <div className="min-w-0">
              <h1 className="font-bold text-gray-900 text-sm truncate">{mobileTitle}</h1>
              <p className="text-[10px] text-gray-400 truncate">
                {COMPANY_NAME} · RIF {COMPANY_RIF}
              </p>
            </div>
          </div>
          {isAdmin && <NotificationBell compact />}
        </header>

        <div className="flex-1 overflow-auto app-main-scroll min-h-0">
          <div key={currentPath} className="page-enter min-h-0">
          <Routes>
            <Route path="/" element={<Navigate to={defaultHome} replace />} />
            <Route
              path="/dashboard"
              element={
                isAdmin ? <DashboardModule /> : <Navigate to={defaultHome} replace />
              }
            />
            <Route
              path="/clients"
              element={
                isQuesero ? <Navigate to={defaultHome} replace /> : <ClientsModule user={user} />
              }
            />
            <Route
              path="/sellers"
              element={
                isAdmin ? <SellersModule /> : <Navigate to={defaultHome} replace />
              }
            />
            <Route
              path="/queseros"
              element={
                isAdmin ? <QueserosModule /> : <Navigate to={defaultHome} replace />
              }
            />
            <Route
              path="/sin-acceso"
              element={
                <div className="p-6 text-sm text-gray-600">
                  Tu cuenta no tiene permisos asignados. Pide al administrador que active leche o quesos.
                </div>
              }
            />
            <Route
              path="/products"
              element={
                isAdmin ? (
                  <ProductsModule user={user} />
                ) : (
                  <Navigate to={defaultHome} replace />
                )
              }
            />
            <Route
              path="/invoices"
              element={
                isQuesero ? <Navigate to={defaultHome} replace /> : <InvoicesModule user={user} />
              }
            />
            <Route
              path="/payments"
              element={
                isQuesero ? <Navigate to={defaultHome} replace /> : <PaymentsModule user={user} />
              }
            />
            <Route
              path="/providers"
              element={
                canMilk ? (
                  <ProvidersModule user={user} />
                ) : (
                  <Navigate to={defaultHome} replace />
                )
              }
            />
            <Route
              path="/cheeses"
              element={
                canCheese ? (
                  <CheesesModule user={user} />
                ) : (
                  <Navigate to={defaultHome} replace />
                )
              }
            />
            <Route
              path="/maturation"
              element={
                isAdmin ? (
                  <MaturationModule />
                ) : (
                  <Navigate to={defaultHome} replace />
                )
              }
            />
            <Route
              path="/provider-payments"
              element={
                isAdmin ? (
                  <ProviderPaymentsModule />
                ) : (
                  <Navigate to={defaultHome} replace />
                )
              }
            />
            <Route
              path="/expenses"
              element={
                isAdmin ? <ExpensesModule /> : <Navigate to={defaultHome} replace />
              }
            />
            <Route
              path="/employees"
              element={
                isAdmin ? <EmployeesModule /> : <Navigate to={defaultHome} replace />
              }
            />
            <Route
              path="/payroll"
              element={
                isAdmin ? <PayrollFinanceModule /> : <Navigate to={defaultHome} replace />
              }
            />
            <Route
              path="/invoice-activity"
              element={
                isAdmin ? <InvoiceActivityModule /> : <Navigate to={defaultHome} replace />
              }
            />
            <Route
              path="/backup"
              element={
                isAdmin ? <BackupModule /> : <Navigate to={defaultHome} replace />
              }
            />
            <Route path="*" element={<Navigate to={defaultHome} replace />} />
          </Routes>
          </div>
        </div>
      </main>

      <MobileBottomNav
        onLogout={handleLogout}
        userName={user.fullName || user.username}
        user={user}
        role={user.role || "admin"}
        permissions={user.permissions || {}}
        onOpenProfile={() => setIsProfileOpen(true)}
      />
      <ProfileSettingsModal open={isProfileOpen} onClose={() => setIsProfileOpen(false)} />
      <BcvRateDisplay isMobile={isMobile} />
    </div>
  );

  return isAdmin ? <NotificationProvider>{shell}</NotificationProvider> : shell;
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
